import {Component, Input, OnInit} from '@angular/core';
import {
    BaseStepperFormComponent,
    filterList,
    LoadingService,
    Menu,
    MenuCategory,
    MenuCategoryItem,
    MenuCategoryItemGroup,
    MenuCategoryService,
    MenuItem,
    MenuItemGroup,
    MenuSelectionItem,
    MenuService,
    sortAlphabetical,
    sortBySortOrder,
    ToastService
} from 'brewbill-lib';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ModalController} from '@ionic/angular';

@Component({
    selector: 'bb-edit-menu-category',
    templateUrl: './edit-menu-category.component.html',
    styleUrls: ['./edit-menu-category.component.scss'],
})
export class EditMenuCategoryComponent extends BaseStepperFormComponent implements OnInit {
    informationFormGroup: FormGroup = new FormGroup({});
    @Input() menuCategory: MenuCategory;
    @Input() tab: number;
    @Input() addMenuItem: any;

    protected readonly sortAlphabetical = sortAlphabetical;

    menu: Menu;
    selectedItems = [];
    availableItems = [];

    constructor(
        modalController: ModalController,
        private menuService: MenuService,
        private menuCategoryService: MenuCategoryService,
        private toastService: ToastService,
        private loadingService: LoadingService,
        private formBuilder: FormBuilder
    ) {
        super(modalController);
    }

    ngOnInit() {
        this.menu = this.menuService.currentValue;
        this.menuCategory = this.menuCategory == null ? new MenuCategory() : this.menuCategory;

        this.selectedItems = this.menuCategory.menuItems == null ? [] : [
            ...this.menuCategory.menuItems, ...this.menuCategory.menuItemGroups
        ].sort((a: any, b: any) => a.sortOrder - b.sortOrder);

        this.availableItems = [...this.menu.menuItems, ...this.menu.menuItemGroups]
            .sort((a: any, b: any) => {
                if (a.name < b.name) {
                    return -1;
                }
                if (a.name > b.name) {
                    return 1;
                }
                return 0;
            });

        this.informationFormGroup = this.formBuilder.group({
            id: this.menuCategory.id,
            parentId: this.menu.id,
            name: [this.menuCategory.name, Validators.required],
            description: this.menuCategory.description,
            sortOrder: this.menuCategory.sortOrder,
            featured: this.menuCategory.featured,
            color: [!!this.menuCategory && !!this.menuCategory.color ? this.menuCategory.color : '#ececec', Validators.required],
            contrastColor: [
                !!this.menuCategory && !!this.menuCategory.contrastColor
                    ? this.menuCategory.contrastColor : '#000000', Validators.required
            ],
        });

        this.subscribe(this.menuService.current.subscribe(m => {
            this.menu = !!m ? new Menu(m) : null;
        }));
    }

    filterByName(items: any[], filter: string) {
        return filterList(items, filter, 'name');
    }

    selectItem(item: MenuItem | MenuItemGroup) {
        if (!this.selectedItems.some(obj => {
            return (!this.isMenuItemGroup(item) && !this.isMenuCategoryItemGroup(obj) && obj.menuItem.id === item.id)
                || (this.isMenuItemGroup(item) && this.isMenuCategoryItemGroup(obj) && obj.menuItemGroup.id === item.id);
        })) {
            if (this.isMenuItemGroup(item)) {
                const menuCategoryItemGroup = new MenuCategoryItemGroup();
                menuCategoryItemGroup.menuItemGroup = item as MenuItemGroup;
                menuCategoryItemGroup.sortOrder = this.selectedItems.length + 1;
                this.selectedItems = [...this.selectedItems, menuCategoryItemGroup];
            } else {
                const menuCategoryItem = new MenuCategoryItem();
                menuCategoryItem.menuItem = item as MenuItem;
                menuCategoryItem.name = item.name;
                menuCategoryItem.sortOrder = this.selectedItems.length + 1;
                this.selectedItems = [...this.selectedItems, menuCategoryItem];
            }
        }
    }

    async openAddMenuItem() {
        const l = await this.addMenuItem(null, true);
        if (l != null) {
            this.selectItem(l);
        }
    }

    removeItem(item: MenuSelectionItem) {
        this.selectedItems = this.selectedItems.filter(t => t.menuItem.id !== item.menuItem.id);
    }

    markItems(items: any[], selectedItems: any[], selectionParam: string, disabledParam: string) {
        items.forEach(i => {
            i[selectionParam] = selectedItems.some(s => s.menuItem.id === i.id);
        });
    }

    updateSelectedItems(items: any[]) {
        const update = this.selectedItems.filter(t =>
            items.some(i => i.id === t.menuItem.id));

        items.filter(i => !update.some(t => t.menuItem.id === i.id)).forEach(m => {
            update.push(new MenuSelectionItem({
                menuItem: m as MenuItem,
                sortOrder: update.length
            }));
        });

        this.selectedItems = sortBySortOrder(update);
    }

    async onSubmit() {
        this.loadingService.present();
        const mc: MenuCategory = new MenuCategory(this.informationFormGroup.value);
        mc.menuItems = this.selectedItems.filter(i => !this.isMenuCategoryItemGroup(i));
        mc.menuItemGroups = this.selectedItems.filter(i => this.isMenuCategoryItemGroup(i));

        if (mc.id != null) {
            this.menuCategoryService.update(mc).subscribe(
                (cat: MenuCategory) => {
                    this.loadingService.dismiss();
                    this.close(cat);
                }, error => {
                    if (error.status === 422) {
                        this.toastService.error('The category is out of sync from a previous change. Please refresh to make changes.');
                    }
                    this.close();
                }
            );
        } else {
            this.menuCategoryService.create(mc).subscribe(
                (cat: MenuCategory) => {
                    this.loadingService.dismiss();
                    this.close(cat);
                }
            );
        }
    }

    isMenuCategoryItemGroup(item: any) {
        return !!item && !!item.menuItemGroup;
    }

    isMenuItemGroup(item: any) {
        return !!item && !!item.menuItems;
    }

    setValue(field, val) {
        this.informationFormGroup.get(field).setValue(val);
    }
}
